import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BedDouble, Loader2, Plus, Trash2, Upload, ImagePlus, UserPlus, Phone, Save, Printer,
} from "lucide-react";
import { toast } from "sonner";

const emptyRoom = {
  room_number: "",
  bed_type: "",
  category: "",
  view: "",
  level: "",
  accessible: false,
  lift_connected: false,
  connecting: false,
  connecting_room_number: "",
  max_occupancy: 2,
  notes: "",
  held: false,
};

const ROLE_PRESETS = ["Reception", "Concierge", "Front Desk", "Spa", "Restaurant", "Housekeeping", "Other"];

export default function Rooms() {
  const { eventId, currentEvent, refreshEvents } = useOutletContext();
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(emptyRoom);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignRoom, setAssignRoom] = useState(null);
  const [assignGuestId, setAssignGuestId] = useState("");
  const [assignSlots, setAssignSlots] = useState("2");
  const [assignExtraBeds, setAssignExtraBeds] = useState("0");
  const [mediaRoom, setMediaRoom] = useState(null);
  const fileRef = useRef();
  const mediaRef = useRef();

  const [hotelName, setHotelName] = useState("");
  const [hotelAddress, setHotelAddress] = useState("");
  const [hotelContacts, setHotelContacts] = useState([]);
  const [savingHotel, setSavingHotel] = useState(false);

  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const [r, g, a, ev] = await Promise.all([
        api.get(`/rooms/${eventId}`),
        api.get(`/guests/${eventId}`),
        api.get(`/room-assignments/${eventId}`),
        api.get(`/events/${eventId}`),
      ]);
      setRooms(r.data);
      setGuests(g.data);
      setAssignments(a.data);
      setHotelName(ev.data.hotel_name || "");
      setHotelAddress(ev.data.hotel_address || "");
      setHotelContacts(ev.data.hotel_contacts || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [eventId]);

  const guestMap = useMemo(() => Object.fromEntries(guests.map((g) => [g.id, g])), [guests]);

  const filtered = rooms.filter((r) => {
    if (filter === "all") return true;
    return r.occupancy_status === filter;
  });

  const openNew = () => {
    setForm(emptyRoom);
    setEditId(null);
    setEditOpen(true);
  };

  const openEdit = (room) => {
    setForm({ ...emptyRoom, ...room });
    setEditId(room.id);
    setEditOpen(true);
  };

  const saveRoom = async () => {
    if (!form.room_number?.trim()) return toast.error("Room number is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        max_occupancy: Number(form.max_occupancy) || 2,
      };
      if (editId) await api.put(`/rooms/${eventId}/${editId}`, payload);
      else await api.post(`/rooms/${eventId}`, payload);
      toast.success(editId ? "Room updated" : "Room added");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save room");
    } finally {
      setSaving(false);
    }
  };

  const removeRoom = async (id) => {
    if (!window.confirm("Delete this room and its assignments?")) return;
    await api.delete(`/rooms/${eventId}/${id}`);
    toast.success("Room deleted");
    load();
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/rooms/${eventId}/import`, fd);
      const parts = [`${data.inserted} new`];
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      toast.success(`Rooms import: ${parts.join(", ")}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    }
  };

  const openAssign = (room) => {
    setAssignRoom(room);
    setAssignGuestId("");
    setAssignSlots(String(Math.min(2, room.slots_remaining || 2) || 1));
    setAssignExtraBeds("0");
    setAssignOpen(true);
  };

  const saveAssign = async () => {
    if (!assignGuestId) return toast.error("Select a guest");
    try {
      await api.post("/room-assignments", {
        room_id: assignRoom.id,
        guest_id: assignGuestId,
        slots_used: Number(assignSlots) || 1,
        extra_beds: Number(assignExtraBeds) || 0,
      });
      toast.success("Guest assigned");
      setAssignOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not assign");
    }
  };

  const unassign = async (assignmentId) => {
    await api.delete(`/room-assignments/${assignmentId}`);
    toast.success("Assignment removed");
    load();
  };

  const uploadMedia = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !mediaRoom) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/rooms/${eventId}/${mediaRoom.id}/media`, fd);
      toast.success("Media uploaded");
      load();
      const { data } = await api.get(`/rooms/${eventId}`);
      const updated = data.find((r) => r.id === mediaRoom.id);
      if (updated) setMediaRoom(updated);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    }
  };

  const deleteMedia = async (mediaId) => {
    await api.delete(`/rooms/${eventId}/${mediaRoom.id}/media/${mediaId}`);
    toast.success("Media removed");
    const { data } = await api.get(`/rooms/${eventId}`);
    setRooms(data);
    const updated = data.find((r) => r.id === mediaRoom.id);
    if (updated) setMediaRoom(updated);
  };

  const saveHotel = async () => {
    setSavingHotel(true);
    try {
      await api.patch(`/events/${eventId}/hotel`, {
        hotel_name: hotelName,
        hotel_address: hotelAddress,
        hotel_contacts: hotelContacts,
      });
      await refreshEvents?.();
      toast.success("Hotel contacts saved");
    } catch {
      toast.error("Could not save hotel info");
    } finally {
      setSavingHotel(false);
    }
  };

  const setContact = (idx, key, val) => {
    const next = [...hotelContacts];
    next[idx] = { ...next[idx], [key]: val };
    setHotelContacts(next);
  };

  if (loading)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" />
      </div>
    );

  const allocationRows = rooms
    .map((room) => {
      const roomAssignments = assignments.filter((a) => a.room_id === room.id);
      return { room, roomAssignments };
    })
    .filter(({ roomAssignments }) => roomAssignments.length > 0);

  const extraBedGaps = guests
    .filter((g) => g.rsvp?.extra_bed_required)
    .map((g) => {
      const placed = assignments
        .filter((a) => a.guest_id === g.id)
        .reduce((sum, a) => sum + (Number(a.extra_beds) || 0), 0);
      const asked = Number(g.rsvp.extra_beds) || 1;
      const roomNumbers = assignments
        .filter((a) => a.guest_id === g.id)
        .map((a) => rooms.find((r) => r.id === a.room_id)?.room_number)
        .filter(Boolean);
      return { guest: g, asked, placed, roomNumbers };
    })
    .filter((row) => row.placed < row.asked);

  return (
    <div className="space-y-6" data-testid="rooms-page">
      <style>{`
        .room-print-sheet { display: none; }
        @media print {
          body * { visibility: hidden; }
          .room-print-sheet, .room-print-sheet * { visibility: visible; }
          .room-print-sheet {
            display: block;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white;
            color: black;
            font-family: Georgia, "Times New Roman", serif;
          }
        }
      `}</style>
      <div className="room-print-sheet" data-testid="room-print-sheet">
        <h1 style={{ fontSize: "22px", margin: "0 0 4px" }}>Room allocation</h1>
        <p style={{ margin: "0 0 12px", fontSize: "13px" }}>
          {[currentEvent?.name, hotelName, currentEvent?.start_date && currentEvent?.end_date ? `${currentEvent.start_date} – ${currentEvent.end_date}` : currentEvent?.start_date].filter(Boolean).join(" · ")}
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
          <thead>
            <tr>
              {["Room", "Type", "Guest", "In room", "Extra beds to place", "Guest requested", "Notes"].map((heading) => (
                <th key={heading} style={{ borderBottom: "1px solid #000", textAlign: "left", padding: "6px 8px" }}>{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allocationRows.map(({ room, roomAssignments }) => roomAssignments.map((a) => {
              const guest = guestMap[a.guest_id];
              const asked = guest?.rsvp?.extra_bed_required ? (Number(guest.rsvp.extra_beds) || 1) : 0;
              return (
                <tr key={a.id}>
                  <td style={{ borderBottom: "1px solid #ccc", padding: "6px 8px", fontWeight: 600 }}>{room.room_number}</td>
                  <td style={{ borderBottom: "1px solid #ccc", padding: "6px 8px" }}>{[room.category, room.bed_type].filter(Boolean).join(" · ")}</td>
                  <td style={{ borderBottom: "1px solid #ccc", padding: "6px 8px" }}>
                    {guest?.primary_guest_name || "Guest"}
                    {guest?.family_name ? ` · ${guest.family_name}` : ""}
                  </td>
                  <td style={{ borderBottom: "1px solid #ccc", padding: "6px 8px" }}>{a.slots_used}</td>
                  <td style={{ borderBottom: "1px solid #ccc", padding: "6px 8px" }}>{Number(a.extra_beds) || 0}</td>
                  <td style={{ borderBottom: "1px solid #ccc", padding: "6px 8px" }}>{asked ? `${asked} extra bed${asked === 1 ? "" : "s"}` : "—"}</td>
                  <td style={{ borderBottom: "1px solid #ccc", padding: "6px 8px" }}>{a.notes || room.notes || ""}</td>
                </tr>
              );
            }))}
            {allocationRows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: "12px 8px" }}>No rooms have been assigned yet.</td>
              </tr>
            )}
          </tbody>
        </table>
        {extraBedGaps.length > 0 && (
          <div style={{ marginTop: "18px" }}>
            <h2 style={{ fontSize: "16px", margin: "0 0 8px" }}>Extra bed requests still to place</h2>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
              <thead>
                <tr>
                  {["Guest", "Requested", "Placed", "Rooms"].map((heading) => (
                    <th key={heading} style={{ borderBottom: "1px solid #000", textAlign: "left", padding: "6px 8px" }}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {extraBedGaps.map((row) => (
                  <tr key={row.guest.id}>
                    <td style={{ borderBottom: "1px solid #ccc", padding: "6px 8px" }}>
                      {row.guest.primary_guest_name}{row.guest.family_name ? ` · ${row.guest.family_name}` : ""}
                    </td>
                    <td style={{ borderBottom: "1px solid #ccc", padding: "6px 8px" }}>{row.asked}</td>
                    <td style={{ borderBottom: "1px solid #ccc", padding: "6px 8px" }}>{row.placed}</td>
                    <td style={{ borderBottom: "1px solid #ccc", padding: "6px 8px" }}>{row.roomNumbers.join(", ") || "Not assigned"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Rooms</h1>
          <p className="text-sm text-[#525252]">
            Inventory for {currentEvent?.name || "this event"} · {rooms.length} rooms
          </p>
          <p className="mt-1 text-xs text-[#737373]">
            Import a hotel sheet with columns ROOM NO, TYPE, CATEGORY, VIEW, LEVEL (e.g. Samode Palace). Other attributes stay empty until you edit them.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={importFile} />
          <Button variant="outline" onClick={() => window.print()} data-testid="print-rooms">
            <Printer className="mr-2 h-4 w-4" /> Print for hotel
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-rooms">
            <Upload className="mr-2 h-4 w-4" /> Import Excel
          </Button>
          <Button onClick={openNew} className="bg-[#1A362D] hover:bg-[#12271f]" data-testid="add-room">
            <Plus className="mr-2 h-4 w-4" /> Add Room
          </Button>
        </div>
      </div>

      {/* Hotel contacts */}
      <div className="rounded-md border border-[#E5E5E5] bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-[#1A362D]" />
            <h2 className="text-sm font-semibold uppercase tracking-wide text-[#525252]">Hotel contacts</h2>
          </div>
          <Button size="sm" onClick={saveHotel} disabled={savingHotel} className="bg-[#1A362D] hover:bg-[#12271f]">
            {savingHotel ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-3.5 w-3.5" /> Save</>}
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Hotel name</Label>
            <Input value={hotelName} onChange={(e) => setHotelName(e.target.value)} data-testid="hotel-name" />
          </div>
          <div className="space-y-2">
            <Label>Address</Label>
            <Input value={hotelAddress} onChange={(e) => setHotelAddress(e.target.value)} />
          </div>
        </div>
        <div className="mt-4 space-y-3">
          {hotelContacts.map((c, idx) => (
            <div key={c.id || idx} className="grid gap-2 rounded-md border border-[#E5E5E5] p-3 md:grid-cols-6">
              <Select value={c.role || "Reception"} onValueChange={(v) => setContact(idx, "role", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_PRESETS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Name" value={c.name || ""} onChange={(e) => setContact(idx, "name", e.target.value)} />
              <Input placeholder="Phone" value={c.phone || ""} onChange={(e) => setContact(idx, "phone", e.target.value)} />
              <Input placeholder="Email" value={c.email || ""} onChange={(e) => setContact(idx, "email", e.target.value)} />
              <Input placeholder="Notes / hours" value={c.notes || ""} onChange={(e) => setContact(idx, "notes", e.target.value)} />
              <Button variant="ghost" size="icon" onClick={() => setHotelContacts(hotelContacts.filter((_, i) => i !== idx))}>
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHotelContacts([...hotelContacts, { id: "", role: "Reception", name: "", phone: "", email: "", notes: "" }])}
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> Add contact
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", "available", "partial", "full", "held"].map((f) => (
          <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} className={filter === f ? "bg-[#1A362D]" : ""} onClick={() => setFilter(f)}>
            {f}
          </Button>
        ))}
      </div>

      <div className="rounded-md border border-[#E5E5E5] bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Room</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>View / Level</TableHead>
              <TableHead>Features</TableHead>
              <TableHead>Occupancy</TableHead>
              <TableHead>Assignees</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((room) => {
              const roomAssignments = assignments.filter((a) => a.room_id === room.id);
              return (
                <TableRow key={room.id}>
                  <TableCell className="font-medium">{room.room_number}</TableCell>
                  <TableCell>{room.bed_type}</TableCell>
                  <TableCell>{room.category}</TableCell>
                  <TableCell className="max-w-[160px] text-xs text-[#525252]">
                    {room.view}<br />{room.level}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {room.accessible && <Badge variant="outline">Accessible</Badge>}
                      {room.lift_connected && <Badge variant="outline">Lift</Badge>}
                      {room.connecting && <Badge variant="outline">Connecting</Badge>}
                      {room.held && <Badge className="bg-amber-100 text-amber-800">Held</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {room.slots_used}/{room.max_occupancy}
                    <span className="ml-1 text-xs text-[#78716C]">({room.occupancy_status})</span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {roomAssignments.map((a) => (
                      <div key={a.id} className="mb-1 flex items-center gap-1">
                        <span>
                          {guestMap[a.guest_id]?.primary_guest_name || a.guest_id} ({a.slots_used})
                          {a.extra_beds ? ` · +${a.extra_beds} bed` : ""}
                          {guestMap[a.guest_id]?.rsvp?.extra_bed_required ? " · asked extra bed" : ""}
                        </span>
                        <button type="button" className="text-red-600" onClick={() => unassign(a.id)}>×</button>
                      </div>
                    ))}
                  </TableCell>
                  <TableCell className="space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openAssign(room)} disabled={room.slots_remaining <= 0 || room.held}>
                      <UserPlus className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setMediaRoom(room)}>
                      <ImagePlus className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(room)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => removeRoom(room.id)}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-[#78716C]">
                  <BedDouble className="mx-auto mb-2 h-6 w-6 opacity-40" />
                  No rooms yet — import the hotel list or add rooms manually.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit room dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit room" : "Add room"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["room_number", "Room number"],
              ["bed_type", "Bed type"],
              ["category", "Category"],
              ["view", "View"],
              ["level", "Level"],
              ["connecting_room_number", "Connecting room #"],
              ["max_occupancy", "Max occupancy"],
            ].map(([key, label]) => (
              <div key={key} className="space-y-1">
                <Label>{label}</Label>
                <Input
                  type={key === "max_occupancy" ? "number" : "text"}
                  value={form[key] ?? ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              </div>
            ))}
            <div className="col-span-2 space-y-1">
              <Label>Notes</Label>
              <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {[
              ["accessible", "Accessible"],
              ["lift_connected", "Lift connected"],
              ["connecting", "Connecting"],
              ["held", "On hold"],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between rounded border px-3 py-2">
                <span className="text-sm">{label}</span>
                <Switch checked={!!form[key]} onCheckedChange={(v) => setForm({ ...form, [key]: v })} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={saveRoom} disabled={saving} className="bg-[#1A362D]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign guest to room {assignRoom?.room_number}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#525252]">
            Remaining capacity: {assignRoom?.slots_remaining ?? 0} of {assignRoom?.max_occupancy ?? 2}
          </p>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Guest</Label>
              <Select value={assignGuestId} onValueChange={setAssignGuestId}>
                <SelectTrigger data-testid="assign-guest"><SelectValue placeholder="Select guest" /></SelectTrigger>
                <SelectContent>
                  {guests.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.primary_guest_name}{g.family_name ? ` (${g.family_name})` : ""}
                      {g.rsvp?.accessible_room_required ? " · accessible" : ""}
                      {g.rsvp?.extra_bed_required ? ` · extra bed ×${g.rsvp.extra_beds || 1}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Slots used</Label>
              <Select value={assignSlots} onValueChange={setAssignSlots}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: Math.max(1, assignRoom?.slots_remaining || 1) }, (_, n) => n + 1).map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Extra beds</Label>
              <Select value={assignExtraBeds} onValueChange={setAssignExtraBeds}>
                <SelectTrigger data-testid="room-extra-beds"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">None</SelectItem>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[#737373]">The same family can also be assigned to another room.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveAssign} className="bg-[#1A362D]">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media dialog */}
      <Dialog open={!!mediaRoom} onOpenChange={(o) => !o && setMediaRoom(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Media — Room {mediaRoom?.room_number}</DialogTitle>
          </DialogHeader>
          <input ref={mediaRef} type="file" accept="image/*,video/*" className="hidden" onChange={uploadMedia} />
          <Button variant="outline" onClick={() => mediaRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" /> Upload photo or video
          </Button>
          <div className="mt-3 grid gap-3">
            {(mediaRoom?.media || []).map((m) => (
              <div key={m.id} className="flex items-start gap-3 rounded border p-2">
                {m.media_type === "video" ? (
                  <video src={mediaUrl(m.url)} className="h-20 w-28 rounded object-cover" controls />
                ) : (
                  <img src={mediaUrl(m.url)} alt="" className="h-20 w-28 rounded object-cover" />
                )}
                <Button size="sm" variant="ghost" onClick={() => deleteMedia(m.id)}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ))}
            {(mediaRoom?.media || []).length === 0 && (
              <p className="text-sm text-[#78716C]">No media yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
